/**
 * Workflow Editor Component
 *
 * Main workflow editor that combines canvas, toolbar, and config panel.
 * Supports real-time execution visualization with step-by-step status updates.
 *
 * Layout:
 * - Top: Header + Toolbar
 * - Middle: Canvas (left) + NodeConfigPanel (right, always visible)
 * - Bottom: ExecutionProgressBar (compact) + ExecutionDrawer (expandable)
 * - Modal: ExecutionHistoryModal (separate)
 */

import React, { useEffect, useCallback, useState } from 'react';
import { ReactFlowProvider } from '@xyflow/react';
import { toast } from 'sonner';

import { WorkflowCanvas } from './WorkflowCanvas';
import { WorkflowToolbar } from './WorkflowToolbar';
import { NodeConfigPanel } from './NodeConfigPanel';
import { RunWorkflowDialog } from './RunWorkflowDialog';
import { ExecutionProgressBar } from './ExecutionProgressBar';
import { ExecutionDrawer } from './ExecutionDrawer';
import { ExecutionHistoryModal } from './ExecutionHistoryModal';
import {
  useWorkflowStore,
  useIsDirty,
  useWorkflowMetadata,
  useIsExecuting,
} from '../hooks/useWorkflowStore';
import {
  useWorkflow,
  useCreateWorkflow,
  useUpdateWorkflow,
  usePollExecution,
  useResumeUserRun,
} from '../hooks/useImageWorkflows';
import type { Workflow, WorkflowExecution, StepStatus } from '@/types/imageWorkflow';

interface WorkflowEditorProps {
  /** Workflow ID to edit, null for new workflow */
  workflowId?: string | null;
  /** Callback when workflow is saved */
  onSave?: (workflow: Workflow) => void;
  /** Callback when navigating back */
  onBack?: () => void;
}

export const WorkflowEditor: React.FC<WorkflowEditorProps> = ({
  workflowId,
  onSave,
  onBack,
}) => {
  const [showRunDialog, setShowRunDialog] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [isDrawerExpanded, setIsDrawerExpanded] = useState(false);

  // Store actions
  const loadWorkflow = useWorkflowStore((s) => s.loadWorkflow);
  const initNewWorkflow = useWorkflowStore((s) => s.initNewWorkflow);
  const markClean = useWorkflowStore((s) => s.markClean);
  const getDefinition = useWorkflowStore((s) => s.getDefinition);
  const settings = useWorkflowStore((s) => s.settings);
  const nodes = useWorkflowStore((s) => s.nodes);

  // Execution actions
  const startExecution = useWorkflowStore((s) => s.startExecution);
  const setNodeExecutionStatus = useWorkflowStore((s) => s.setNodeExecutionStatus);
  const setNodeExecutionResult = useWorkflowStore((s) => s.setNodeExecutionResult);
  const completeExecution = useWorkflowStore((s) => s.completeExecution);

  // Store state
  const isDirty = useIsDirty();
  const metadata = useWorkflowMetadata();
  const isExecuting = useIsExecuting();

  // API hooks
  const { data: existingWorkflow, isLoading: isLoadingWorkflow } = useWorkflow(
    workflowId ?? null
  );
  const createWorkflow = useCreateWorkflow();
  const updateWorkflow = useUpdateWorkflow();
  const pollExecution = usePollExecution();
  const resumeUserRun = useResumeUserRun();

  // Load workflow on mount or when ID changes
  useEffect(() => {
    if (workflowId && existingWorkflow) {
      loadWorkflow(existingWorkflow);
    } else if (!workflowId) {
      initNewWorkflow();
    }
  }, [workflowId, existingWorkflow, loadWorkflow, initNewWorkflow]);

  // Auto-expand drawer when execution starts
  useEffect(() => {
    if (isExecuting) {
      setIsDrawerExpanded(true);
    }
  }, [isExecuting]);

  // Validate workflow key
  const validateKey = (key: string): string | null => {
    if (!key || key.trim() === '') {
      return 'Key 是必填项';
    }
    if (!/^[a-z][a-z0-9_]*$/.test(key)) {
      return 'Key 必须以小写字母开头，只能包含小写字母、数字和下划线';
    }
    if (key.length > 64) {
      return 'Key 最长64个字符';
    }
    return null;
  };

  // Handle save
  const handleSave = useCallback(async () => {
    // Validate key before saving
    const keyError = validateKey(metadata.key);
    if (keyError) {
      toast.error(keyError);
      return;
    }

    const definition = getDefinition();

    try {
      if (metadata.id) {
        // Update existing workflow
        const updated = await updateWorkflow.mutateAsync({
          workflowId: metadata.id,
          data: {
            name: metadata.name,
            key: metadata.key,
            description: metadata.description,
            definition,
            prompt_strategy: settings.promptStrategy,
            prompt_config: settings.promptConfig,
            default_storage_mode: settings.storageMode,
          },
        });
        markClean();
        toast.success('Workflow saved');
        onSave?.(updated);
      } else {
        // Create new workflow
        const created = await createWorkflow.mutateAsync({
          name: metadata.name,
          key: metadata.key,
          description: metadata.description,
          definition,
          prompt_strategy: settings.promptStrategy,
          prompt_config: settings.promptConfig,
          default_storage_mode: settings.storageMode,
        });
        // Update store with new ID
        loadWorkflow(created);
        markClean();
        toast.success('Workflow created');
        onSave?.(created);
      }
    } catch (error) {
      console.error('Save failed:', error);
      toast.error('Failed to save workflow');
    }
  }, [
    metadata,
    settings,
    getDefinition,
    createWorkflow,
    updateWorkflow,
    markClean,
    loadWorkflow,
    onSave,
  ]);

  // Handle run
  const handleRun = useCallback(() => {
    // Save first if dirty
    if (isDirty) {
      toast.info('Please save the workflow before running');
      return;
    }
    setShowRunDialog(true);
  }, [isDirty]);

  // Handle execution start - called when workflow starts executing
  const handleExecutionStart = useCallback(
    (executionId: string) => {
      // Initialize execution tracking
      startExecution(executionId);
      setShowRunDialog(false);
      setIsDrawerExpanded(true);

      // Start polling for updates
      pollExecution.mutate({
        executionId,
        onUpdate: (execution: WorkflowExecution) => {
          // Update node statuses based on user_runs[0].steps (assuming single user for now)
          if (execution.user_runs && execution.user_runs.length > 0) {
            const userRun = execution.user_runs[0];
            if (userRun.steps) {
              for (const step of userRun.steps) {
                setNodeExecutionStatus(step.node_key, step.status as StepStatus);
                setNodeExecutionResult(step.node_key, step);
              }
            }
          }

          // Check if execution is complete
          if (
            execution.status === 'completed' ||
            execution.status === 'failed' ||
            execution.status === 'partial'
          ) {
            completeExecution(execution.status, execution.error_summary ?? undefined);
            if (execution.status === 'completed') {
              toast.success('Workflow execution completed!');
            } else if (execution.status === 'failed') {
              toast.error('Workflow execution failed');
            } else {
              toast.warning('Workflow execution partially completed');
            }
          }
        },
      });
    },
    [startExecution, pollExecution, setNodeExecutionStatus, setNodeExecutionResult, completeExecution]
  );

  // Handle back navigation with unsaved changes warning
  const handleBack = useCallback(() => {
    if (isDirty) {
      const confirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to leave?'
      );
      if (!confirmed) return;
    }
    onBack?.();
  }, [isDirty, onBack]);

  // Handle open history modal
  const handleOpenHistory = useCallback(() => {
    setShowHistoryModal(true);
  }, []);

  // Handle resume from failed execution
  const handleResume = useCallback(
    async (executionId: string, userRunId: string, resumeNodeKey: string) => {
      // Close history modal first
      setShowHistoryModal(false);

      // Initialize execution tracking (this will show the execution state on canvas)
      startExecution(executionId);
      setIsDrawerExpanded(true);

      try {
        // Start resume (async, not waiting for completion)
        await resumeUserRun.mutateAsync({
          executionId,
          userRunId,
          resumeFromNodeKey: resumeNodeKey,
          sync: false, // Don't wait - we'll poll for updates
        });

        // Poll for updates
        pollExecution.mutate({
          executionId,
          onUpdate: (execution: WorkflowExecution) => {
            // Update node statuses based on user_runs[0].steps
            if (execution.user_runs && execution.user_runs.length > 0) {
              const userRun = execution.user_runs[0];
              if (userRun.steps) {
                for (const step of userRun.steps) {
                  setNodeExecutionStatus(step.node_key, step.status as StepStatus);
                  setNodeExecutionResult(step.node_key, step);
                }
              }
            }

            // Check if execution is complete
            if (
              execution.status === 'completed' ||
              execution.status === 'failed' ||
              execution.status === 'partial'
            ) {
              completeExecution(execution.status, execution.error_summary ?? undefined);
              if (execution.status === 'completed') {
                toast.success('Workflow resume completed!');
              } else if (execution.status === 'failed') {
                toast.error('Workflow resume failed');
              } else {
                toast.warning('Workflow resume partially completed');
              }
            }
          },
        });
      } catch (error) {
        console.error('Resume failed:', error);
        toast.error('Failed to resume execution');
        completeExecution('failed', String(error));
      }
    },
    [startExecution, resumeUserRun, pollExecution, setNodeExecutionStatus, setNodeExecutionResult, completeExecution]
  );

  // Loading state
  if (workflowId && isLoadingWorkflow) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2" />
          <p className="text-gray-500">Loading workflow...</p>
        </div>
      </div>
    );
  }

  const isSaving = createWorkflow.isPending || updateWorkflow.isPending;

  return (
    <ReactFlowProvider>
      <div className="h-full flex flex-col">
        {/* Header with back button */}
        <div className="border-b bg-white px-4 py-2 flex items-center gap-4">
          {onBack && (
            <button
              onClick={handleBack}
              className="flex items-center gap-1 text-gray-600 hover:text-gray-900"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
              Back
            </button>
          )}
          <h1 className="text-lg font-semibold text-gray-800">
            {metadata.id ? 'Edit Workflow' : 'New Workflow'}
          </h1>
          {metadata.version > 0 && (
            <span className="text-xs text-gray-400">v{metadata.version}</span>
          )}
        </div>

        {/* Toolbar */}
        <WorkflowToolbar
          onSave={handleSave}
          onRun={handleRun}
          onHistory={handleOpenHistory}
          isSaving={isSaving}
          isRunning={isExecuting || pollExecution.isPending}
          hasWorkflowId={!!metadata.id}
        />

        {/* Main content - Canvas + Config Panel */}
        <div className="flex-1 flex overflow-hidden">
          {/* Canvas - read-only during execution */}
          <div className="flex-1">
            <WorkflowCanvas readOnly={isExecuting || pollExecution.isPending} />
          </div>

          {/* Config panel - always visible */}
          <NodeConfigPanel />
        </div>

        {/* Bottom execution area */}
        {isDrawerExpanded ? (
          <ExecutionDrawer
            isOpen={isDrawerExpanded}
            onClose={() => setIsDrawerExpanded(false)}
            onOpenHistory={handleOpenHistory}
          />
        ) : (
          <ExecutionProgressBar
            totalNodes={nodes.length}
            isExpanded={isDrawerExpanded}
            onToggleExpand={() => setIsDrawerExpanded(true)}
            onOpenHistory={handleOpenHistory}
          />
        )}

        {/* Run dialog */}
        {metadata.id && (
          <RunWorkflowDialog
            isOpen={showRunDialog}
            workflowId={metadata.id}
            workflowName={metadata.name}
            onClose={() => setShowRunDialog(false)}
            onSuccess={handleExecutionStart}
          />
        )}

        {/* History modal */}
        <ExecutionHistoryModal
          isOpen={showHistoryModal}
          onClose={() => setShowHistoryModal(false)}
          workflowId={metadata.id}
          onResume={handleResume}
          isResuming={resumeUserRun.isPending}
        />
      </div>
    </ReactFlowProvider>
  );
};

export default WorkflowEditor;
